import React, { useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import { useParams } from 'react-router-dom';

import useSession from 'core/hooks/use-session';
import { StatusPill } from 'core/components/status-pill';
import NotificationIconWhite from 'core/assets/svg/alert-white-bold.svg';
import NotificationIconDark from 'core/assets/svg/alert-dark-green-bold.svg';
import { Label } from 'core';

import { getDraftFooterContent, getFooterState, FOOTER_ACTION, BOTTOM_BAR_TONE } from './draft-footer.helpers';
import useStyles from './draft-footer.styles';

const PRIMARY_ACTION_LABEL = {
  [FOOTER_ACTION.PREVIEW]: 'Preview endorsement',
  [FOOTER_ACTION.SEE_DRAFTS_TABLE]: 'See drafts table'
};

function DraftFooter({ preview, previewDrafts, segment, segments, onSubmit, onViewNotes, onSeeDraftsTable }) {
  const classes = useStyles();
  const { policyId, id } = useParams();
  const { canApproveUWReview } = useSession();

  // Only a preview not yet saved as a draft is lost on leaving; a saved draft reloads from its previewId.
  const hasUnsavedPreview = Boolean(preview?.id) && !preview.draft;
  useEffect(() => {
    if (!hasUnsavedPreview) {
      return undefined;
    }
    window.onbeforeunload = () => true;
    return () => {
      window.onbeforeunload = null;
    };
  }, [hasUnsavedPreview]);

  const { status, effectiveDate, statusChange, isCurrentSegment, isOlderVersion } = getFooterState({
    preview,
    previewDrafts,
    segment,
    segments
  });
  const { title, detail, icon, pill, subline, showViewNotes, primaryAction, bottomBar } = getDraftFooterContent({
    status,
    canApproveUWReview,
    statusChange,
    effectiveDate,
    isCurrentSegment,
    isOlderVersion
  });
  const primaryActionHandlers = {
    [FOOTER_ACTION.PREVIEW]: onSubmit,
    [FOOTER_ACTION.SEE_DRAFTS_TABLE]: onSeeDraftsTable
  };

  return (
    <div className={classes.container}>
      <Grid container className={classes.footerInner}>
        <div className={classes.leftBlock}>
          <img src={icon} alt="" className={classes.draftIcon} />
          <div>
            <div className={classes.titleRow}>
              <Label className={classes.headingTitle} type="subtitle1">
                {title}
              </Label>
              <Label className={classes.headingDetail} type="body1">
                {detail}
              </Label>
              <StatusPill {...pill} />
            </div>
            <Label type="body2">{subline}</Label>
          </div>
        </div>
        <div className={classes.buttons}>
          {showViewNotes && (
            <Button variant="contained" className={classNames(classes.btn, classes.viewNotesBtn)} onClick={onViewNotes}>
              <Label className={classes.btnLabel}>View notes</Label>
            </Button>
          )}
          {primaryAction && (
            <Button
              variant="contained"
              color="primary"
              data-cy={primaryAction === FOOTER_ACTION.PREVIEW ? 'preview-policy-changes' : 'see-drafts-table'}
              onClick={primaryActionHandlers[primaryAction]}
              className={classes.btn}
            >
              <Label className={classes.btnLabel}>{PRIMARY_ACTION_LABEL[primaryAction]}</Label>
            </Button>
          )}
        </div>
      </Grid>
      {bottomBar && (
        <div className={classNames(classes.bottomBar, classes[bottomBar.tone])}>
          <img
            src={bottomBar.tone === BOTTOM_BAR_TONE.ORANGE ? NotificationIconWhite : NotificationIconDark}
            className={classes.bottomBarIcon}
            alt=""
          />
          {bottomBar.text ?? (
            <>
              You're viewing a copy of this policy.
              <a
                className={classes.link}
                href={`/customer/${id}/policy/${policyId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                View the official policy here.
              </a>
            </>
          )}
        </div>
      )}
    </div>
  );
}

DraftFooter.propTypes = {
  preview: PropTypes.object,
  previewDrafts: PropTypes.array,
  segment: PropTypes.object,
  segments: PropTypes.array,
  onSubmit: PropTypes.func,
  onViewNotes: PropTypes.func,
  onSeeDraftsTable: PropTypes.func
};

DraftFooter.defaultProps = {
  preview: undefined,
  previewDrafts: [],
  segment: undefined,
  segments: [],
  onSubmit: undefined,
  onViewNotes: undefined,
  onSeeDraftsTable: undefined
};

export default memo(DraftFooter);
